/**
 * DevicesTab — Lists devices assigned to the area with assign/remove controls.
 *
 * Columns: Device Name (link), Description, Model, Serial Number, Status
 * Actions: Assign Device button, per-row remove icon
 *
 * Status uses the same dp-status pattern as the V2 Devices page:
 *   - Green (dp-status--active):   active, last_active within 30 hours
 *   - Yellow (dp-status--warning): active, last_active > 30 hours (stale) — not
 *     applied to manufacturers that never heartbeat (STALE_EXEMPT_MFG)
 *   - Grey (dp-status--inactive):  not active
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { skipsStaleCheck } from '../../../../../utils/deviceStatus.js';

const STALE_THRESHOLD_MS = 30 * 3600000; // 30 hours

/* Manufacturers on this surface whose devices never heartbeat: last_active never
   advances, so the stale test would permanently show them yellow. 10 is SIP /
   speakers, which report nothing until they are called. */
const STALE_EXEMPT_MFG = [10];

/* ── StatusPopover — wraps a status badge, shows detail on hover ── */
const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!message) return children;

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

const DevicesTab = ({
  areaDevices,
  areaName,
  devEditRights,
  onAssign,
  onRemove,
  // Matches AreasPage's drawer: SIP (manufacture_id 10) and System (mid 1
  // + device_category_id 4/5) devices are restricted — only role weight
  // > 80 may add or remove them. Non-admins still see them in the list
  // for awareness but the remove button is disabled.
  canManageRestrictedDevices = true,
  isRestrictedDevice,
}) => {
  const checkRestricted = (d) =>
    typeof isRestrictedDevice === 'function' ? isRestrictedDevice(d) : false;
  /**
   * Build the device profile link. Uses the unified V2 device URL pattern
   * (/community/devices/:mfgId/:device_id) — Community.jsx routes by mfgId:
   *   1 → Inovonics device profile
   *   4 → PalCare device profile
   *   6 → Wandering (Accutech) profile
   */
  const getDeviceLink = (device) => {
    const mid = parseInt(device?.manufacture_id, 10) || 1;
    return `/community/devices/${mid}/${device.device_id}`;
  };

  // Devices without a per-device profile page render as plain text:
  //   - manufacture_id === 10 (SIP / speakers — no profile)
  //   - manufacture_id === 1 + device_type_id ∈ {10, 11, 56, 57}
  //     (virtual/hardware-only system types — no profile)
  const NON_LINKABLE_SYSTEM_DEVICE_TYPES = new Set([10, 11, 56, 57]);
  const canLinkToProfile = (device) => {
    const mid = parseInt(device?.manufacture_id, 10);
    const tid = parseInt(device?.device_type_id, 10);
    if (mid === 4) return true;
    if (mid === 1) return !NON_LINKABLE_SYSTEM_DEVICE_TYPES.has(tid);
    return false;
  };

  /** Derive status class and label from is_active + last_active */
  const getStatus = (device) => {
    if (!Number(device.is_active)) {
      return { className: 'dp-status--inactive', label: 'Inactive', title: undefined };
    }
    /* Manufacturers that never heartbeat can't be judged on last_active — see
       STALE_EXEMPT_MFG above. Skipping the test rather than faking a timestamp
       keeps the reason visible at the point the badge is chosen. */
    const stale = !skipsStaleCheck(device.manufacture_id, STALE_EXEMPT_MFG)
      && (device.last_active
        ? (new Date() - new Date(device.last_active)) > STALE_THRESHOLD_MS
        : true);
    if (stale) {
      return {
        className: 'dp-status--warning',
        label: 'Active',
        title: 'Device may be stale — test the device to ensure functionality',
      };
    }
    return { className: 'dp-status--active', label: 'Active', title: undefined };
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">
          Assigned Devices
          <span className="pp-section__count">({areaDevices?.length})</span>
        </h3>
        {devEditRights ? (
          <div className="pp-section__actions">
            <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAssign}>
              + Assign Device
            </button>
          </div>
        ) : null}
      </div>
      <div className="pp-section__body pp-section__body--flush">
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead className="dp-thead">
              <tr>
                <th className="dp-th">Device Name</th>
                <th className="dp-th">Description</th>
                <th className="dp-th">Model</th>
                <th className="dp-th">Serial Number</th>
                <th className="dp-th" style={{ textAlign: 'center' }}>Status</th>
                {devEditRights ? <th className="dp-th" style={{ textAlign: 'center', width: '80px' }}>Remove</th> : null}
              </tr>
            </thead>
            <tbody>
              {areaDevices.map((device) => {
                const status = getStatus(device);
                return (
                  <tr key={device.device_id} className="dp-tr">
                    <td className="dp-td">
                      {canLinkToProfile(device) ? (
                        <Link to={getDeviceLink(device)} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {device.device_name || '—'}
                        </Link>
                      ) : (
                        <span className="dp-cell-text" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {device.device_name || '—'}
                        </span>
                      )}
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.descriptions || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.model_name || device.manufacture_model || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text dp-cell-text--mono">{device.manufacture_uuid || '—'}</span>
                    </td>
                    <td className="dp-td" style={{ textAlign: 'center' }}>
                      <StatusPopover message={status.title}>
                        <span className={`dp-status ${status.className}`}>
                          <span className="dp-status__dot" aria-hidden="true" />
                          {status.label}
                        </span>
                      </StatusPopover>
                    </td>
                    {devEditRights ? (
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        {(() => {
                          const removeBlocked = checkRestricted(device) && !canManageRestrictedDevices;
                          return (
                            <button
                              className="ap-remove-btn"
                              onClick={() => onRemove(device)}
                              disabled={removeBlocked}
                              title={
                                removeBlocked
                                  ? 'Only administrators may remove this device type.'
                                  : `Remove ${device.device_name || 'device'} from ${areaName}`
                              }
                            >
                              ✕
                            </button>
                          );
                        })()}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {areaDevices.length === 0 ? (
                <tr className="dp-tr">
                  <td className="dp-td" colSpan={devEditRights ? 6 : 5} style={{ textAlign: 'center' }}>
                    <span className="dp-cell-text dp-cell-text--muted">No devices assigned to this area</span>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DevicesTab;
